import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity } from 'react-native'
import { Button, Text, Spinner, Toggle, Icon } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { savePurchaseMethods, deletePurchaseMethod, checkIdUniquenes } from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import SelectionIconButton from '../../components/SelectionIconButton'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { constantsAtom, storesDictAtom } from '../../global/AtomsProvider'
import { HintView } from '../../components/HintView'
import { useColors } from '../../utils/useColors'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'

const purchaseIcons = ['house-user', 'walking', 'taxi', 'truck', 'biking', 'instagram', 'whatsapp', 'dolly', 'clipboard-list', 'building', 'city',  'hotel', 'houzz', ]

export default function PurchaseMethodDetailScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const colors = useColors()
  const [context, setContext] = useContext(UserContext)
  const { detail, detailIndex, allData } = route.params
  const storesDict = useAtomValue(storesDictAtom)
  const constants = useAtomValue(constantsAtom)
  const [id, setId] = useState(detail ? (detail.id || '') : ('purchaseMethod-' + Date.now()))
  const [name, setName] = useState(detail ? (detail.name || '') : '')
  const [icon, setIcon] = useState(detail ? (detail.icon || '') : '')
  const [iconText, setIconText] = useState(detail ? (detail.iconText || '') : '')
  const [activeInStore, setActiveInStore] = useState(detail ? (detail.activeInStore || {}) : {})
  const [priceModificators, setPriceModificators] = useState(detail ? (detail.priceModificators || []) : [])
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // Emit detail data for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[PURCHASE_METHOD_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        name: detail.name,
        icon: detail.icon,
        iconText: detail.iconText,
        activeInStore: detail.activeInStore,
      })));
    }
  }, [detail]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            if (constants?.purchaseMethods.data.length === 1) {
              alert(t('settings.atLeastOnePurchaseMethodError'))
              setIsLoading(false)
              return
            }
            deletePurchaseMethod(context.db, detailIndex, allData)
            navigation.goBack()
          }}
          iconName='trash-outline'
        />
      ),
      title: (detail?.name || '')
    })
  }, [])

  const TrashIcon = (style) => (
    <Icon {...style} name='trash-outline'/>
  );

  async function save() {
    setIsLoading(true)
    let uniqueId = true
    if (detailIndex == allData.length) {
      uniqueId = checkIdUniquenes(id, constants)
    }
    if (name !== '' && id !== '' && icon !== '' && uniqueId) {
      setSaveError('')
      let info = {
        name,
        id,
        icon,
        iconText,
        activeInStore,
        priceModificators
      }
      try {
        await savePurchaseMethods(context.db, info, detailIndex, allData)
        setSuccessMessageVisible(true)
        setIsLoading(false)
      } catch (error) {
        Sentry.captureException(error)
        console.log(error)
        setSaveError(t('error'))
        setIsLoading(false)
      }
    } else if (uniqueId) {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
    } else {
      setSaveError(t('settings.uniqueIdError'))
      setIsLoading(false)
    }
  }

  

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id}
        </Text>}
    
        <Text category='label' style={styles.label}>
          {t('settings.name')}
        </Text>
        <StyledTextInput 
          value={name}
          onChangeText={text => setName(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />

        <Text category='label' style={styles.label}>
          {t('settings.iconAndText')}
        </Text>
        <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
          { purchaseIcons.map((item, index) => (
            <SelectionIconButton 
              key={item+index}
              onPress={() => {setIcon(item)}} 
              text={iconText}
              iconName={item}
              selected={item === icon}
              style={{marginRight: 5}}
            />
          )) }
        </View>
        <StyledTextInput 
          value={iconText}
          style={{marginTop: 10}}
          onChangeText={text => setIconText(text)}
          placeholder={t('settings.textUnderIcon')}
          returnKeyType='done'
        />

        <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
          <Text category='label' style={{marginRight: 5}}>{t('settings.priceModificators')}</Text>
          <TouchableOpacity onPress={() => {
            setPriceModificators([...priceModificators, {name: '', value: ''}])
          }}>
            <Icon name='plus-circle-outline' height={16} width={16} fill={colors.primary}/>
          </TouchableOpacity>
        </View>
        <HintView
          text={t('settings.priceModificatorsHint')}
          iconName='info-outline'
          style={{marginBottom: 10}}
        />
        { priceModificators.length > 0 ? priceModificators.map((item, index) => (
          <View key={index} style={{flexDirection: 'row', alignItems: 'center'}}>
            <StyledTextInput
              value={item.name}
              onChangeText={text => {
                var newPriceModificators = [...priceModificators]
                newPriceModificators[index].name = text
                setPriceModificators(newPriceModificators)
              }}
              returnKeyType='done'
              style={{flex: 1, marginRight: 5}}
              placeholder={t('settings.name')}
            />
            <StyledTextInput
              value={item.value + ''}
              onChangeText={text => {
                var newPriceModificators = [...priceModificators]
                newPriceModificators[index].value = parseFloat(text)
                setPriceModificators(newPriceModificators)
              }}
              returnKeyType='done'
              keyboardType='numeric'
              style={{flex: 1}}
              placeholder={t('settings.value')}
            />
            <Button style={styles.button} appearance='ghost' status='danger' accessoryLeft={TrashIcon} onPress={() => {
              var newPriceModificators = [...priceModificators]
              newPriceModificators.splice(index, 1)
              setPriceModificators(newPriceModificators)
            }}/>
          </View>
        )) : (
          <View>
            <Text appearance='hint' style={{textAlign: 'center'}}>
              {t('settings.noPriceModificators')}
            </Text>
          </View>
        )}

        <Text category='label' style={styles.label}>
          {t('settings.settingsByStore')}  
        </Text>
        { Object.entries(storesDict).map(([key, value]) => (
          <View key={key} style={{marginBottom: 10}}>
            <Toggle 
              checked={activeInStore[key]}
              onChange={(value) => {
                var newActive = {...activeInStore}
                newActive[key] = value
                setActiveInStore(newActive)
              }}
              style={styles.toggleField}
            >
              {storesDict?.[key].name}
            </Toggle>
          </View>
          ))
        }
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  toggleField: {
    marginBottom: 5,
    alignSelf: 'flex-start'
  },
});